import { sanitizeHtml } from 'javascripts/utils';
import $ from 'jquery';
import { debounce, memoize } from 'lodash';

const getHtmlWidthDangerously = function (
  htmlElement,
  size,
  width,
  classes,
  fontWeight
) {
  if (classes == null) {
    classes = '';
  }
  if (fontWeight == null) {
    fontWeight = 'normal';
  }
  let div = $(sanitizeHtml(`<div class='measure-text ${classes}'>`));
  // function and variable name clearly indicate a contract of html safety.
  // eslint-disable-next-line no-unsanitized/method
  div.html(htmlElement);
  div = div[0];
  div.style.position = 'absolute';
  div.style.top = '-9999px';
  div.style.left = '-9999px';
  div.style.fontFamily = "'Inter', Helvetica, 'Segoe UI', Arial, sans-serif";
  div.style.fontWeight = fontWeight;
  div.style.fontSize = `${size}px`;
  if (width) {
    div.style.maxWidth = width;
  } else {
    div.style.maxWidth = 'initial';
  }

  document.body.appendChild(div);
  size = { width: div.offsetWidth, height: div.offsetHeight };
  document.body.removeChild(div);
  return size;
};

const getTextWidth = function (text, size, width, classes, fontWeight) {
  let htmlHolder = $(`<span></span>`);
  // \xa0 is hex equivalent to &nbsp;
  // Use jquery text to avoid XSS attacks
  htmlHolder.text(`${text}\xa0\xa0`);
  return getHtmlWidthDangerously(htmlHolder, size, width, classes, fontWeight);
};

const createMemoizedHtmlWidthDangerously = (
  size,
  classes,
  fontWeight,
  width
) => {
  return memoize(htmlElement =>
    getHtmlWidthDangerously(htmlElement, size, width || '', classes, fontWeight)
  );
};

const createMemoizedTextWidth = (size, classes, fontWeight, width) => {
  return memoize(text =>
    getTextWidth(text, size, width || '', classes, fontWeight)
  );
};

const calculateScrollBarSize = function () {
  const scrollDiv = document.createElement('div');
  scrollDiv.style.width = '100px';
  scrollDiv.style.height = '100px';
  scrollDiv.style.overflow = 'scroll';
  scrollDiv.style.position = 'absolute';
  scrollDiv.style.top = '-9999px';
  document.body.appendChild(scrollDiv);

  // Get the scrollbar width
  let scrollBarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

  // Delete the DIV
  document.body.removeChild(scrollDiv);

  // Hidden scroll bars in safar still have some width even though
  // they should not making them seem to be 1 width fixes this issue
  if (scrollBarWidth === 0 && navigator.userAgent.indexOf('Safari')) {
    scrollBarWidth = 1;
  }

  return scrollBarWidth;
};

const scrollBarSize = memoize(calculateScrollBarSize);

// Batch all the callbacks together in one requestAnimationFrame that will
// be fired 100ms after the last call
let callbacks = [];
const debouncedExecuteBatchedCallbacks = debounce(() => {
  requestAnimationFrame(() => {
    const callbacksCopy = callbacks;
    callbacks = [];

    callbacksCopy.forEach(callback => callback());
  });
}, 100);

const asyncGetHtmlWidthDangerously = async function (
  htmlElement,
  size,
  width,
  classes,
  fontWeight
) {
  if (classes == null) {
    classes = '';
  }
  if (fontWeight == null) {
    fontWeight = 'normal';
  }
  let div = $(sanitizeHtml(`<div class='measure-text ${classes}'>`));
  // function and variable name clearly indicate a contract of html safety.
  // eslint-disable-next-line no-unsanitized/method
  div.html(htmlElement);
  div = div[0];
  div.style.position = 'absolute';
  div.style.top = '-9999px';
  div.style.left = '-9999px';
  div.style.fontFamily = "'Inter', Helvetica, 'Segoe UI', Arial, sans-serif";
  div.style.fontWeight = fontWeight;
  div.style.fontSize = `${size}px`;
  if (width) {
    div.style.maxWidth = width;
  } else {
    div.style.maxWidth = 'initial';
  }

  document.body.appendChild(div);

  // Wrap the logic after the debugger statement inside a Promise
  const sizePromise = new Promise(resolve => {
    callbacks.push(() =>
      resolve({ width: div.offsetWidth, height: div.offsetHeight })
    );

    debouncedExecuteBatchedCallbacks();
  });

  // Await the sizePromise to get the size asynchronously
  const renderedSize = await sizePromise;
  // Check if the measure container exists and is still on the DOM
  // by checking if it's parent element is still document.body
  if (div && div.parentElement === document.body) {
    document.body.removeChild(div);
  }
  return renderedSize;
};

const asyncCreateMemoizedHtmlWidthDangerously = (
  size,
  classes,
  fontWeight,
  width
) => {
  return memoize(htmlElement =>
    asyncGetHtmlWidthDangerously(
      htmlElement,
      size,
      width || '',
      classes,
      fontWeight
    )
  );
};

export {
  getTextWidth,
  getHtmlWidthDangerously,
  createMemoizedHtmlWidthDangerously,
  createMemoizedTextWidth,
  scrollBarSize,
  asyncGetHtmlWidthDangerously,
  asyncCreateMemoizedHtmlWidthDangerously,
};
